import * as React from 'react';
import { Alert, Button, Modal, Steps } from 'antd';
import { observer } from 'mobx-react-lite';
import { RulesImportVisualStore } from '../stores';
import type { RuleImportOrigin, PendingRuleImportAttempt } from '../types';
import { RulesImportTable } from './RulesImportTable';
import { RulesImportMapperTable } from './RulesImportMapperTable';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { RulesImportResolutionActions, RulesImportResolutionContent } from './RulesImportResolution';
import {
    getTestProjectInputEditorTitle,
    TestProjectInputEditorActions,
    TestProjectInputEditorContent
} from '../../test_projects/components/TestProjectInputEditor';
import type RulesImportResolutionStore from '../stores/RulesImportResolutionStore';

type Props = {
    store: RulesImportVisualStore;
    resolution: RulesImportResolutionStore;
    warningMsg?: string;
    origin: RuleImportOrigin;
};

// Focus the screen title on navigation, including projects with the same name. Selection changes leave focus alone.
const ImportTitle: React.FC<{ projectId?: string; step: PendingRuleImportAttempt['step'] }> = ({
    children,
    projectId,
    step
}) => {
    const ref = React.useRef<HTMLSpanElement>(null);

    React.useEffect(() => {
        ref.current?.focus();
    }, [projectId, step]);

    return (
        <span ref={ref} tabIndex={-1}>
            {children}
        </span>
    );
};

const RulesImportModal: React.FC<Props> = ({ store, resolution, origin, warningMsg }) => {
    const attempt = store.pendingAttempt;

    if (!attempt || attempt.origin !== origin) {
        return null;
    }

    const dataStore = resolution.dataStore;
    const editorStore = resolution.editorStore;
    const editingDraft = editorStore.getDraftFor(dataStore);
    const resolving = store.isResolvingTestProjectUsage;
    const confirmation = attempt.step === 'confirmation';
    const importsAllRules = attempt.target.type !== 'rules';
    const busy = store.isLoading || (resolving && editorStore.isSubmitting);
    const cancelDisabled = busy || !resolution.canCancelDialog;
    const modalClassName = resolving
        ? 'alpha-portal-dialog rule-import rule-import-journey scrollable'
        : 'alpha-portal-dialog rule-import scrollable full-screen';

    const onMapInputs = () => resolution.openInputMapping();

    const onCancel = () => {
        if (cancelDisabled) {
            return;
        }

        if (editingDraft) {
            editorStore.cancelEditing();
            return;
        }

        if (resolving) {
            resolution.cancelDialog();
        } else {
            store.cancelDialog();
        }
    };

    const getTitle = () => {
        if (editingDraft) {
            return getTestProjectInputEditorTitle(editingDraft);
        }

        if (resolving && dataStore?.inputsChange && dataStore.testProject) {
            return `${dataStore.testProject.name} updated`;
        }

        if (resolving && resolution.isReadyToImport) {
            return 'Rule package ready to import';
        }

        if (resolving && dataStore) {
            return dataStore.testProject?.name || 'Review Test Project';
        }

        if (resolving) {
            return attempt.scope === 'full' ? 'Cannot import rule package' : 'Cannot replace tags';
        }

        return confirmation ? 'Import rules' : 'Imported Results';
    };

    const getProgressStep = () => {
        if (editingDraft) {
            return 2;
        }

        if (dataStore?.inputsChange || resolution.isReadyToImport) {
            return 3;
        }

        return dataStore ? 1 : 0;
    };

    const getFooter = () => {
        if (editingDraft) {
            return (
                <TestProjectInputEditorActions
                    store={editorStore}
                    draft={editingDraft}
                    onMapInputs={onMapInputs}
                    onCancel={editorStore.cancelEditing}
                    saveLabel="Save mapping"
                />
            );
        }

        if (resolving) {
            return <RulesImportResolutionActions store={store} resolution={resolution} />;
        }

        if (confirmation) {
            return (
                <>
                    <Button
                        className="light"
                        data-id="rules-import-dialog-cancel"
                        disabled={busy}
                        onClick={store.backToMapping}
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button
                        className="dark"
                        data-id="rules-import-dialog-proceed"
                        disabled={!store.canAdvanceImport}
                        onClick={() => store.importRules()}
                        size="large"
                        type="primary"
                    >
                        Proceed
                    </Button>
                </>
            );
        }

        return (
            <>
                <Button
                    className="light"
                    data-id="iota-add-group-dialog-cancel"
                    disabled={busy}
                    onClick={onCancel}
                    size="large"
                >
                    Cancel
                </Button>
                <Button
                    className="dark"
                    data-id="rules-import-page-import-button"
                    disabled={!store.canAdvanceImport}
                    onClick={store.handleImportClick}
                    size="large"
                    type="primary"
                >
                    {attempt.step !== 'selection' || !attempt.mapperTableData.length ? 'Import' : 'Next'}
                </Button>
            </>
        );
    };

    const getContent = () => {
        if (editingDraft) {
            return <TestProjectInputEditorContent store={editorStore} draft={editingDraft} />;
        }

        if (resolving) {
            return <RulesImportResolutionContent store={store} resolution={resolution} />;
        }

        if (confirmation) {
            return <p>You are about to overwrite all existing rules. Do you really want to proceed?</p>;
        }

        if (store.isLoading) {
            return <LoadingIndicator />;
        }

        return (
            <>
                {warningMsg && <Alert message={warningMsg} style={{ margin: '12px 0' }} type="warning" />}

                {attempt.step === 'selection' && (
                    <RulesImportTable RulesImportUI={store} attempt={attempt} importForIota={importsAllRules} />
                )}

                {attempt.step !== 'selection' && <RulesImportMapperTable RulesImportUI={store} attempt={attempt} />}
            </>
        );
    };

    const title = getTitle();

    return (
        <Modal
            centered
            className={resolving || confirmation || editingDraft ? modalClassName : `${modalClassName} table`}
            closable={false}
            destroyOnClose={false}
            footer={getFooter()}
            keyboard={!cancelDisabled}
            maskClosable={false}
            onCancel={onCancel}
            title={
                <ImportTitle key={title} projectId={dataStore?.testProjectId} step={attempt.step}>
                    {title}
                </ImportTitle>
            }
            visible={store.isDialogVisible}
            width={resolving ? 800 : 1300}
        >
            {resolving && (
                <Steps
                    className="rule-import-progress"
                    size="small"
                    labelPlacement="vertical"
                    current={getProgressStep()}
                >
                    <Steps.Step title="Import blocked" />
                    <Steps.Step title="Review project" />
                    <Steps.Step title="Map inputs" />
                    <Steps.Step title="Return to import" />
                </Steps>
            )}

            {!resolving && (store.usageStatus === 'error' || store.usageStatus === 'idle') && (
                <Alert
                    action={<Button onClick={() => store.refreshBlockers()}>Retry</Button>}
                    message="Could not check Test Project usage. Retry before importing."
                    style={{ margin: '12px 0' }}
                    type="error"
                />
            )}
            {getContent()}
        </Modal>
    );
};

export default observer(RulesImportModal);
